import { chunk, compact, filter, inRange, includes, isEmpty, map, range, some, sum } from "lodash"
import { ChampionshipBetPick, ChampionshipBetTeam, PickValue, TChampionshipBet, TeamId } from "./types"

export const NUMBER_OF_RANKS = 12
export const ROUND2_OFFSET_OPPONENTS = 4
export const ROUND2_OFFSET_WINNERS = 8
export const ROUND3_OFFSET = 12

export const ROUND2_OPPONENT_TO_WINNER = 4

const ROUND1_NUMBER_OF_TEAMS = 8
const ROUND2_NUMBER_OF_PREQUALIFIED_TEAMS = 4

export const pickIsSet= (pick: ChampionshipBetPick) => !!pick && pick !== "0" && pick !== 0

export const getRound1 = (
  picks: ChampionshipBetPick[] | undefined,
  teams: (ChampionshipBetTeam | null)[],
  points: PickValue[],
  event_state: TChampionshipBet["event_state"],
) => {
  return {
    pairings: chunk(teams.slice(0, ROUND1_NUMBER_OF_TEAMS), 2),
    pickedPercent: Math.floor(filter(picks?.slice(0, ROUND2_OFFSET_OPPONENTS), pickIsSet).length / (ROUND1_NUMBER_OF_TEAMS / 2) * 100),
    isOpen: event_state === "open",
    getPick: (index: number) => ({ pickId: picks?.[index], points: points[index] }),
    resetIndices: range(ROUND2_OFFSET_OPPONENTS, ROUND3_OFFSET),
  }
}

export const getRound2 = (
  picks: ChampionshipBetPick[] | undefined,
  teams: (ChampionshipBetTeam | null)[],
  points: PickValue[],
  event_state: TChampionshipBet["event_state"],
) => {
  const options = compact(map(picks?.slice(0,ROUND2_OFFSET_OPPONENTS), (pick) => teams.find((team) => team?.id === pick)))
  const opponents = picks?.slice(ROUND2_OFFSET_OPPONENTS, ROUND2_OFFSET_WINNERS)
  const opponentsPickIndices = range(ROUND2_OFFSET_OPPONENTS, ROUND2_OFFSET_WINNERS)
  const winnersPickIndices = range(ROUND2_OFFSET_WINNERS, ROUND3_OFFSET)
  const teamIndices = range(ROUND2_OFFSET_WINNERS, ROUND2_OFFSET_WINNERS + ROUND2_NUMBER_OF_PREQUALIFIED_TEAMS)
  return {
    options,
    teams: teams.slice(ROUND1_NUMBER_OF_TEAMS, ROUND1_NUMBER_OF_TEAMS + ROUND2_NUMBER_OF_PREQUALIFIED_TEAMS),
    opponentsPickIndices,
    pickedPercent: Math.floor(filter(picks?.slice(ROUND2_OFFSET_OPPONENTS, ROUND3_OFFSET), pickIsSet).length / ROUND1_NUMBER_OF_TEAMS * 100),
    isOpen: event_state === "open",
    resetIndices: range(ROUND2_OFFSET_WINNERS, ROUND3_OFFSET),
    getPicks: (index: number) => {
      const opponentIndex = opponentsPickIndices[index]
      const winnerIndex = winnersPickIndices[index]
      return {
        pickIndex: teamIndices[index],
        pickedOpponent: picks?.[opponentIndex],
        pickedWinner: picks?.[winnerIndex],
        winnerIndex,
        opponentIndex,
        team: options.find((team) => opponents?.[index] === team?.id),
        isAlreadyInSquad: (teamId: TeamId) => some(opponentsPickIndices, (pi) => picks?.[pi] === teamId),
        winnerPoints: points[winnerIndex],
        opponentPoints: points[opponentIndex],
      }
    }
  }
}

const constructRound3OptionsFunction = (teams: (ChampionshipBetTeam | null)[], picks: ChampionshipBetPick[] | undefined) => {
  const round1Pairings = chunk(teams.slice(0, ROUND1_NUMBER_OF_TEAMS), 2)
  const round1WinnerPicks = picks?.slice(0, ROUND2_OFFSET_OPPONENTS)
  const round1Losers = compact(round1Pairings.map(([left, right], index) => {
    const pick = round1WinnerPicks?.[index]
    if (left && right && pickIsSet(pick)) {
      return left.id === pick ? right : left
    }
    return undefined
  }))
  const round2OpponentPicks = picks?.slice(ROUND2_OFFSET_OPPONENTS, ROUND2_OFFSET_WINNERS)
  const round2Pairings = teams.slice(ROUND1_NUMBER_OF_TEAMS).reduce(
    (prev, team, index) => {
      const result = [...prev]
      const pairing = [] as (ChampionshipBetTeam | null)[]
      pairing[0] = team
      pairing[1] = teams.find((t) => t ? t.id === round2OpponentPicks?.[index] : false) || null
      result.push(pairing)
      return result
    }
  , [] as (ChampionshipBetTeam | null)[][])
  const round2WinnerPicks = picks?.slice(ROUND2_OFFSET_WINNERS, ROUND3_OFFSET)
  const [round2Winners, round2Losers] = round2Pairings.reduce(([winners, losers], [left, right], index) => {
    const newWinners = [...winners]
    const newLosers = [...losers]
    if (left && left.id === round2WinnerPicks?.[index]) {
      newWinners.push(left)
      if (right) {
        newLosers.push(right)
      }
    } else if (right && right.id === round2WinnerPicks?.[index]) {
      newWinners.push(right)
      if (left) {
        newLosers.push(left)
      }
    }
    return [newWinners, newLosers]
  }, [[],[]] as ChampionshipBetTeam[][])
  const getOptions = (index: number) => {
    if (inRange(index, 0, 4)) {
      return round2Winners
    } else if (inRange(index, 4, 8)) {
      return round2Losers
    } else {
      return round1Losers
    }
  }
  return getOptions
}

export const getRound3 = (
  picks: ChampionshipBetPick[] | undefined,
  teams: (ChampionshipBetTeam | null)[],
  points: PickValue[],
  event_state: TChampionshipBet["event_state"],
) => {
  const pickIndices = range(ROUND3_OFFSET, ROUND3_OFFSET + NUMBER_OF_RANKS)
  const currentPick = (index: number) => picks?.[pickIndices[index]]
  const findTeam = (index: number) => teams.find((team) => currentPick(index) === team?.id)
  const round3Points = points.slice(ROUND3_OFFSET)
  const pointsSum: PickValue = some(round3Points, (point) => !!point || point === 0) ? sum(round3Points) : undefined
  const getOptions = constructRound3OptionsFunction(teams, picks)

  return {
    getOptions,
    ranking: picks?.slice(ROUND3_OFFSET, ROUND3_OFFSET + NUMBER_OF_RANKS),
    ranks: range(0, NUMBER_OF_RANKS),
    pickedPercent: Math.floor(filter(picks?.slice(ROUND3_OFFSET), pickIsSet).length / NUMBER_OF_RANKS * 100),
    pickIndices,
    currentPick,
    findTeam,
    isCorrect: (index: number) => points?.[index + ROUND3_OFFSET] === -1,
    isOpen: event_state === "open",
    points: pointsSum,
  }
}

export const buildPicks = (item: ChampionshipBetTeam, prev: ChampionshipBetPick[], pickIndices: number[], pickIndex: number, resetIndices?: number[]) => {
  const swapBuffer = prev[pickIndex]
  const swapIndex = pickIndices.find((pi) => {
    return prev[pi] === item?.id
  })
  const newPicks = prev.concat()
  newPicks[pickIndex] = item?.id || undefined
  if (swapIndex !== undefined) {
    newPicks[swapIndex] = swapBuffer
  }
  if (resetIndices) {
    for (let resetIndex of resetIndices) {
      if (prev[resetIndex] === item.id) {
        newPicks[resetIndex] = undefined
      }
      if ((swapIndex || swapIndex === 0) && prev[resetIndex] === prev[swapIndex]) {
        newPicks[resetIndex] = undefined
      }
    }
  }
  return newPicks
}

export const getOverview = (picks: ChampionshipBetPick[] | undefined) => {
  return {
    pickedPercentRound1: Math.floor(filter(picks?.slice(0, ROUND2_OFFSET_OPPONENTS), pickIsSet).length / (ROUND1_NUMBER_OF_TEAMS / 2) * 100),
    pickedPercentRound2: Math.floor(filter(picks?.slice(ROUND2_OFFSET_OPPONENTS, ROUND3_OFFSET), pickIsSet).length / ROUND1_NUMBER_OF_TEAMS * 100),
    pickedPercentRound3: Math.floor(filter(picks?.slice(ROUND3_OFFSET), pickIsSet).length / NUMBER_OF_RANKS * 100),
  }
}
